import { NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { VirtualScrollerComponent, VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { IPageInfo } from '@iharbeck/ngx-virtual-scroller/lib/ngx-virtual-scroller.component';
import { PushPipe } from '@ngrx/component';
import { deepEqual } from 'fast-equals';
import { BehaviorSubject, Subject, combineLatest, merge, Observable } from 'rxjs';
import { debounceTime, skip, takeUntil } from 'rxjs/operators';

import { filterBoolean, trackByProp } from '@recruitee/common';

import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent, DropdownWidth } from '../../common/dropdown/dropdown.component';
import { SpinnerComponent } from '../../common/spinner/spinner.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  OverlayPosition,
} from '../../core/overlay/overlay-directives';
import { InlineOpenStrategy, OpenStrategy } from './default-strategies/open-strategy';
import { FooterResolverDirective } from './default-templates/footer/footer-resolver.directive';
import { HeaderResolverDirective } from './default-templates/header/header-resolver.directive';
import { ListItemResolverDirective } from './default-templates/list-item/list-item-resolver.directive';
import { SelectedItemResolverDirective } from './default-templates/selected-item/selected-item-resolver.directive';
import { TriggerResolverDirective } from './default-templates/trigger/trigger-resolver.directive';
import { WithKeyNavDirective } from './features/keyboard-feature.directive';
import { SelectController } from './select-controller.service';
import { HeaderTemplate, Select2Item, SELECT_CONTROLLER } from './types';
import { ItemWrapperComponent } from './utils/item-wrapper.component';

export const defaultCompareItemsFn = <Item extends Select2Item>(
  a: Item,
  b: Item | { value: Item['value'] },
) => deepEqual(a.value, b.value);

const vsCompareItemsFallback = (a: Select2Item | undefined, b: Select2Item | undefined) => a === b;

@Component({
  selector: 'rt-select2',
  standalone: true,
  imports: [
    DropdownComponent,
    VirtualScrollerModule,
    DropdownItemComponent,
    NgTemplateOutlet,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    PushPipe,
    NgStyle,
    ItemWrapperComponent,
    SpinnerComponent,
  ],
  templateUrl: './select2.component.html',
  styleUrls: ['./select2.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // eslint-disable-next-line @angular-eslint/no-forward-ref
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Select2Component), multi: true },
    { provide: SELECT_CONTROLLER, useExisting: SelectController },
    SelectController,
  ],
  hostDirectives: [
    TriggerResolverDirective,
    ListItemResolverDirective,
    SelectedItemResolverDirective,
    HeaderResolverDirective,
    FooterResolverDirective,
    WithKeyNavDirective,
  ],
})
export class Select2Component<Item extends Select2Item> implements ControlValueAccessor {
  @ViewChild('dropdownConnectedOverlay', { static: true })
  public dropdownConnectedOverlay: ConnectedOverlayDirective;

  @ViewChild('dropdownTemplate', { static: true })
  public dropdownTemplate: TemplateRef<void>;

  @ViewChild('dropdownTemplate', { static: true, read: ViewContainerRef })
  public dropdownTemplateVcRef: ViewContainerRef;

  @ViewChild('triggerOverlay', { static: true })
  public triggerOverlay: ConnectedOverlayDirective;

  @ViewChild('inlineTriggerOrigin', { static: true })
  public inlineTriggerOrigin: OverlayOriginDirective;

  @ViewChild('overlayTriggerOrigin')
  public set overlayTriggerOrigin(origin: OverlayOriginDirective | undefined) {
    this.overlayTriggerOrigin$.next(origin ?? null);
  }

  @ViewChild('virtual')
  public set virtualScroll(component: VirtualScrollerComponent | undefined) {
    this.selectController.setVirtualScroll(component);
  }

  @Input() public items: Item[];
  @Input() public overlayWidth: number | string;
  @Input() public overlayMinWidth: number | string;
  @Input() public overlayPosition: OverlayPosition[];
  @Input() public preselected: Item | Item[] | null;
  @Input() public compareItemsFn = defaultCompareItemsFn;
  @Input() public autoClose: boolean = true;
  @Input() public itemsLoading: boolean = false;
  @Input() public vsCompareItems: (a: Item | undefined, b: Item | undefined) => boolean;

  @Output() public vsUpdate = new EventEmitter<IPageInfo>(true);
  @Output() public vsEnd = new EventEmitter<IPageInfo>(true);
  @Output() public selection = new EventEmitter<any>();
  @Output() public close = new EventEmitter<void>();
  @Output() public clear = new EventEmitter<void>();
  @Output() public uiItemSelect = <Observable<Item>>this.selectController.select$.asObservable();

  public trackByValue = trackByProp<Select2Item>({ prop: 'value' });
  public trackByTemplate = trackByProp<HeaderTemplate>({ prop: 'template' });
  public virtualScrollStyle: Partial<CSSStyleDeclaration>;
  public virtualScrollIndexOffset: number = 0;
  public DropdownWidth = DropdownWidth;
  public vsCompareItemsFallback = vsCompareItemsFallback;

  protected openStrategy: OpenStrategy =
    inject(OpenStrategy, { optional: true }) ?? new InlineOpenStrategy();

  private overlayTriggerOrigin$ = new BehaviorSubject<OverlayOriginDirective | null>(null);
  private ngOnDestroy$ = new Subject<void>();
  private onChange: (v: Select2Item | Select2Item[]) => void;

  constructor(
    public selectController: SelectController,
    private cdRef: ChangeDetectorRef,
    private triggerResolver: TriggerResolverDirective,
    private listItemResolver: ListItemResolverDirective,
    private selectedItemResolver: SelectedItemResolverDirective,
    private headerResolverDirective: HeaderResolverDirective,
    private footerResolverDirective: FooterResolverDirective,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.compareItemsFn) {
      this.selectController.itemSelector.setCompareItemsFn(
        this.compareItemsFn ?? defaultCompareItemsFn,
      );
    }

    if (changes.preselected) {
      this.selectController.itemSelector.setPreselected(<Select2Item>this.preselected);
    }

    if (changes.items && this.items) {
      this.selectController.setItems(this.items);
    }

    if (
      changes.overlayWidth ||
      changes.overlayMinWidth ||
      changes.overlayPosition ||
      changes.autoClose
    ) {
      this.openStrategy.setOptions({
        overlayWidth: this.overlayWidth,
        overlayMinWidth: this.overlayMinWidth,
        overlayPosition: this.overlayPosition,
        autoClose: this.autoClose,
      });
    }
  }

  public ngOnInit() {
    this.openStrategy.register({
      inlineTriggerOrigin: this.inlineTriggerOrigin,
      dropdownConnectedOverlay: this.dropdownConnectedOverlay,
      dropdownTemplate: this.dropdownTemplate,
      dropdownTemplateVcRef: this.dropdownTemplateVcRef,
      triggerOverlay: this.triggerOverlay,
      overlayTriggerOrigin$: this.overlayTriggerOrigin$,
    });

    this.openStrategy.init();
    this.selectController.isOpen$.pipe(skip(1), takeUntil(this.ngOnDestroy$)).subscribe(isOpen => {
      if (isOpen) return;

      this.close.emit();
    });
  }

  public ngAfterContentInit() {
    this.resolveTemplates();
    this.handleClear();
    this.handleSelect();
    this.handleVirtualScrollTransition();

    merge(
      this.selectController.navigation$,
      this.selectController.detectChanges$,
      this.selectController.itemSelector.detectChanges$,
    )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public onKeyDown(event: KeyboardEvent) {
    this.selectController.keyDown$.next(event);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onVirtualScrollUpdate(event: IPageInfo) {
    this.virtualScrollIndexOffset = event.startIndexWithBuffer;
    this.vsUpdate.emit(event);
    this.cdRef.detectChanges();
  }

  public onVsEnd(event: IPageInfo) {
    this.vsEnd.emit(event);
  }

  public registerOnChange(fn: (v: Select2Item | Select2Item[]) => void): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public registerOnTouched(fn: any): void {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public setDisabledState(isDisabled: boolean): void {
    this.selectController.setDisabled(isDisabled);
  }

  public writeValue(value: Item['value'] | Array<Item['value']>): void {
    this.selectController.itemSelector.writeValue(value);
    this.selectController.detectChanges$.next();
  }

  public scrollTop() {
    this.virtualScroll?.scrollToPosition(0, 0);
  }

  private resolveTemplates() {
    const headerDirective = this.headerResolverDirective.resolve();
    if (headerDirective) {
      this.selectController.headerTemplateRegister({
        index: 0,
        id: 'header',
        template: headerDirective.templateRef,
      });
    }

    const footerDirective = this.footerResolverDirective.resolve();
    if (footerDirective) {
      this.selectController.footerTemplateRegister({
        index: 0,
        id: 'footer',
        template: footerDirective.templateRef,
      });
    }

    this.selectController.triggerDirective = this.triggerResolver.resolve();
    this.selectController.listItemDirective = this.listItemResolver.resolve();
    this.selectController.selectedItemDirective = this.selectedItemResolver.resolve();
  }

  private handleClear() {
    this.selectController.clear$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      const { itemSelector } = this.selectController;
      itemSelector.clear();
      this.onChange?.(itemSelector.getValue());
      this.clear.emit();
      this.cdRef.detectChanges();
    });
  }

  private handleSelect() {
    this.selectController.select$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(item => {
      this.selectController.itemSelector.select(<Select2Item>item);
    });

    this.selectController.itemSelector.selectionChanged$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        const { itemSelector } = this.selectController;
        const value = itemSelector.getValue();
        this.onChange?.(value);
        itemSelector.writeValue(value);
        this.selection.emit(itemSelector.getSelectedItem());
        this.cdRef.detectChanges();
      });
  }

  private handleVirtualScrollTransition() {
    combineLatest([
      this.selectController.itemsToRender$,
      this.selectController.virtualScroll$.pipe(filterBoolean),
    ])
      .pipe(debounceTime(0), takeUntil(this.ngOnDestroy$))
      .subscribe(([items, virtualScroll]) => {
        const scrollElement: HTMLElement = virtualScroll['contentElementRef'].nativeElement;
        if (items.length > 20) {
          scrollElement.style.position = 'absolute';
          this.virtualScrollStyle = { height: '270px' };
        } else {
          scrollElement.style.position = 'static';
          this.virtualScrollStyle = { maxHeight: '270px' };
        }
        this.cdRef.detectChanges();
      });
  }
}
